import React from 'react'

import { useNavigate } from 'react-router-dom';
import { useIntersectionObserver } from '../utils/useIntersectionObserver';
import { openExternalLink } from '../utils/shared.js';

function RecommendedSuppliers({ selectedCategory, allCategories, onSupplierClick }) {
    const navigate = useNavigate();
    const [allSuppliers, setAllSuppliers] = React.useState([]);
    const [selectedSupplier, setSelectedSupplier] = React.useState(null);

    const handleSupplierSelect = (supplier) => {
        // Track the supplier click for analytics
        try {
            const formData = new FormData();
            formData.append('supplier_name', supplier.name);
            navigator.sendBeacon('/api/track_supplier_click.php', formData);
        } catch (e) {
            console.error("Could not track supplier click:", e);
        }
        setSelectedSupplier(supplier);
    };

    React.useEffect(() => {
        fetch('/api/get_suppliers.php', { credentials: 'include' })
            .then(res => res.json())
            .then(data => {
                // Add the placeholder dynamically
                const placeholder = { name: 'Placeholder 1', isPlaceholder: true, text: 'If you want to have your supplier featured here email: finance@minersmarket.co.za', display_categories: ['all'] };
                // Ensure all suppliers have a valid display_categories array
                const sanitizedData = data.map(supplier => ({...supplier, display_categories: supplier.display_categories || [] }));
                setAllSuppliers([...data, placeholder]);
            })
            .catch(err => console.error("Failed to fetch suppliers:", err));
    }, []);

    // Helper to get all ancestor category slugs for a given category
    const getAncestorSlugs = React.useCallback((categoryId, categories) => {
        const slugs = [];
        let current = categories.find(c => c.id === categoryId);
        while (current && current.parent_id) {
            const parent = categories.find(c => c.id === current.parent_id);
            if (parent) {
                slugs.push(parent.slug);
                current = parent;
            } else {
                break;
            }
        }
        return slugs;
    }, [allCategories]);

    const filteredSuppliers = React.useMemo(() => {
        if (!selectedCategory || !allCategories || allCategories.length === 0) return [];
        
        // Filter out suppliers that are exclusive to the home page from the general marketplace view.
        // This ensures they don't appear on category pages.
        const nonHomePageExclusiveSuppliers = allSuppliers.filter(supplier =>
            !(supplier.display_categories || []).includes('home-page-exclusive')
        );

        // If 'all' is selected, show all non-home-page-exclusive suppliers.
        if (selectedCategory === 'all') {
            // Show suppliers marked for 'all' or the special 'all-products-only' category.
            return nonHomePageExclusiveSuppliers.filter(supplier =>
                (supplier.display_categories || []).includes('all') || (supplier.display_categories || []).includes('all-products-only'));
        }

        const currentCategory = allCategories.find(c => c.slug === selectedCategory);
        
        // If the category doesn't exist in the DB (e.g., 'filters', 'greases' which are table-only categories),
        // fall back to showing suppliers marked for 'all' so the section still appears.
        if (!currentCategory) {
            return nonHomePageExclusiveSuppliers.filter(supplier =>
                (supplier.display_categories || []).includes('all')
            );
        }

        const ancestorSlugs = getAncestorSlugs(currentCategory.id, allCategories);
        // For specific categories, show suppliers marked for 'all' or the specific category, or any ancestor category.
        return nonHomePageExclusiveSuppliers.filter(supplier => {
            const supplierCats = supplier.display_categories || [];
            return (
                supplierCats.includes('all') ||
                supplierCats.includes(currentCategory.slug) ||
                ancestorSlugs.some(slug => supplierCats.includes(slug))
            );
        });
    }, [selectedCategory, allCategories, allSuppliers, getAncestorSlugs]);

    const supplierSectionRef = React.useRef(null);

    const trackSupplierViews = React.useCallback(() => {
        const supplierNames = filteredSuppliers.map(s => s.name);
        if (supplierNames.length > 0) {
            try {
                const payload = JSON.stringify({ suppliers: supplierNames });
                navigator.sendBeacon('/api/track_supplier_view.php', payload);
            } catch (e) {
                console.error("Could not track supplier views:", e);
            }
        }
    }, [filteredSuppliers]);

    useIntersectionObserver(supplierSectionRef, trackSupplierViews);

    // This check must happen *after* all hooks have been called.
    if (filteredSuppliers.length === 0) return null;

    return (
        <div ref={supplierSectionRef} data-name="recommended-suppliers" data-file="components/RecommendedSuppliers.js" className="mb-8 mt-0">
            {/* Mobile Layout - Grid of all suppliers */}
            <div className="md:hidden bg-white/50 backdrop-blur-sm rounded-xl py-3 px-4 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                    <span className="text-[12px] font-black text-gray-900 uppercase tracking-tighter">Partners & Recommended Suppliers</span>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 justify-items-center">
                    {filteredSuppliers.map((supplier, idx) => (
                        supplier.isPlaceholder ? null : (
                            <div 
                                key={`${supplier.name}-${idx}`} 
                                onClick={() => handleSupplierSelect(supplier)} 
                                className="transition-all hover:scale-110 active:scale-95 cursor-pointer group"
                                title={supplier.name}
                            >
                                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-white p-2 sm:p-3 shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden ring-2 ring-transparent group-hover:ring-blue-100">
                                    <img 
                                        src={supplier.logo} 
                                        alt={`${supplier.name} Logo`} 
                                        className="w-full h-full object-contain" 
                                    />
                                </div>
                            </div>
                        )
                    ))}
                </div>
            </div>
            
            {/* Desktop Layout - Grid of all suppliers */}
            <div className="hidden md:block bg-white/50 backdrop-blur-sm rounded-xl py-4 px-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 bg-blue-600 rounded-lg animate-pulse"></div>
                    <span className="text-[12px] font-black text-gray-900 uppercase tracking-tighter">Partners & Recommended Suppliers</span>
                </div>
                <div className="grid grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-4 justify-items-center">
                    {filteredSuppliers.map((supplier, idx) => (
                        supplier.isPlaceholder ? null : (
                            <div 
                                key={`${supplier.name}-${idx}`} 
                                onClick={() => handleSupplierSelect(supplier)} 
                                className="transition-all hover:scale-110 active:scale-95 cursor-pointer group"
                                title={supplier.name}
                            >
                                <div className="w-16 h-16 lg:w-18 lg:h-18 xl:w-20 xl:h-20 rounded-lg bg-white p-3 lg:p-4 shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden ring-2 ring-transparent group-hover:ring-blue-100">
                                    <img 
                                        src={supplier.logo} 
                                        alt={`${supplier.name} Logo`} 
                                        className="w-full h-full object-contain" 
                                    />
                                </div>
                            </div>
                        )
                    ))}
                </div>
            </div>
            {selectedSupplier && (
                <SupplierInfoModal
                    supplier={selectedSupplier}
                    onClose={() => setSelectedSupplier(null)}
                    navigate={navigate}
                />
            )}
        </div>
    );
}

export function SupplierInfoModal({ supplier, onClose, navigate }) {
    if (!supplier) return null;

    const externalLink = supplier.link;
    const internalLink = supplier.internal_link;

    const handleExternal = () => {
        if (!externalLink) return;
        let url = externalLink;
        if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
        openExternalLink(url);
    };

    const handleInternal = () => {
        if (!internalLink) return;
        let path = internalLink.trim();
        // If a full same-origin URL was provided, convert it to an internal route.
        try {
            if (/^https?:\/\//i.test(path)) {
                const url = new URL(path);
                // Only treat as internal if it's the same origin as the app.
                if (url.origin === window.location.origin) {
                    path = url.pathname + url.search + url.hash;
                } else {
                    // Different origin -> open externally instead.
                    onClose();
                    openExternalLink(path);
                    return;
                }
            }
        } catch (e) {
            // If URL parsing fails, fall back to treating it as a path.
        }
        if (!path.startsWith('/')) path = '/' + path;
        onClose();
        navigate(path);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <img src={supplier.logo} alt={`${supplier.name} Logo`} className="h-14 w-14 object-contain rounded bg-gray-50 p-1 flex-shrink-0" />
                        <h3 className="text-xl font-bold text-gray-900 truncate">{supplier.name}</h3>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none flex-shrink-0 ml-2">&times;</button>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line mb-6">{supplier.text}</p>
                <div className="flex flex-wrap gap-3">
                    {externalLink && (
                        <button onClick={handleExternal} className="primary-gradient text-white py-2 px-4 rounded-lg text-sm font-medium">
                            <i className="fas fa-external-link-alt mr-2"></i>Visit Website
                        </button>
                    )}
                    {internalLink && (
                        <button onClick={handleInternal} className="bg-gray-800 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-900">
                            <i className="fas fa-arrow-right mr-2"></i>Open Page
                        </button>
                    )}
                    {!externalLink && !internalLink && (
                        <span className="text-xs text-gray-500">No link has been provided for this supplier.</span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default RecommendedSuppliers;
